/**
 * OfficeDashboardV2Page — Prototype redesign of /home
 *
 * Design direction: Inbox-to-Zero spine + Live Stream side rail + Live Field hero band.
 * See branch claude/fix-attention-section-cutoff-I7NOM commit history.
 *
 * Available at /home/v2 alongside the existing /home dashboard so the team can
 * compare side-by-side before deciding to cut over.
 */

import { getLocaleSync } from '@/utils/locale';
import { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { Sun, Moon, Coffee } from 'lucide-react';
import { LiveFieldHero } from '@/components/dashboard-v2/LiveFieldHero';
import { InboxColumn } from '@/components/dashboard-v2/InboxColumn';
import { StreamColumn } from '@/components/dashboard-v2/StreamColumn';
import { HealthPulse } from '@/components/dashboard-v2/HealthPulse';

export default function OfficeDashboardV2Page() {
  const userName = localStorage.getItem('logged_in_user') || 'there';
  const firstName = userName.split(' ')[0];
  const greeting = useTimeOfDayGreeting();
  const [now, setNow] = useState(() => new Date());

  // Live ticking clock for the header
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 30_000);
    return () => clearInterval(id);
  }, []);

  return (
    <div className="relative min-h-screen overflow-hidden">
      {/* Animated mesh gradient background */}
      <BackgroundOrbs />

      <div className="relative max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-5">
        {/* Greeting band */}
        <header className="flex items-end justify-between gap-4 flex-wrap">
          <div>
            <div className="flex items-center gap-2 text-xs text-theme-muted mb-1.5">
              <greeting.Icon className={`w-3.5 h-3.5 ${greeting.color}`} />
              <LiveClock now={now} />
            </div>
            <h1 className="text-3xl font-light text-theme-primary tracking-tight leading-tight">
              {greeting.label},{' '}
              <span className="text-shimmer font-semibold">{firstName}</span>
            </h1>
          </div>

        </header>

        {/* Bento Row 1: Live Field (8 cols) + Health Pulse 2x2 (4 cols) */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-5 items-stretch">
          <div className="lg:col-span-8">
            <LiveFieldHero />
          </div>
          <div className="lg:col-span-4">
            <HealthPulse />
          </div>
        </div>

        {/* Bento Row 2: Inbox (7 cols) + Stream (5 cols) */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-5 items-stretch">
          <div className="lg:col-span-7">
            <InboxColumn />
          </div>
          <div className="lg:col-span-5">
            <StreamColumn />
          </div>
        </div>

        <p className="text-center text-[10px] text-theme-muted/60 italic">
          Prototype · /home/v2 · feedback welcome
        </p>
      </div>
    </div>
  );
}

// ============================================================================
// Background orbs — animated mesh gradient depth
// ============================================================================

function BackgroundOrbs() {
  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      <motion.div
        className="absolute -top-40 -left-40 w-[480px] h-[480px] rounded-full bg-brand-secondary/[0.05] blur-3xl"
        animate={{ x: [0, 60, 0], y: [0, 40, 0] }}
        transition={{ duration: 20, repeat: Infinity, ease: 'easeInOut' }}
      />
      <motion.div
        className="absolute top-1/3 -right-40 w-[520px] h-[520px] rounded-full bg-blue-500/[0.04] blur-3xl"
        animate={{ x: [0, -40, 0], y: [0, 60, 0] }}
        transition={{ duration: 25, repeat: Infinity, ease: 'easeInOut' }}
      />
      <motion.div
        className="absolute bottom-0 left-1/3 w-[420px] h-[420px] rounded-full bg-purple-500/[0.035] blur-3xl"
        animate={{ x: [0, 40, 0], y: [0, -50, 0] }}
        transition={{ duration: 22, repeat: Infinity, ease: 'easeInOut' }}
      />
    </div>
  );
}

// ============================================================================
// Greeting helpers
// ============================================================================

function useTimeOfDayGreeting() {
  return useMemo(() => {
    const h = new Date().getHours();
    if (h >= 5 && h < 12) {
      return { label: 'Good morning', Icon: Sun, color: 'text-amber-400' };
    }
    if (h >= 12 && h < 17) {
      return { label: 'Good afternoon', Icon: Sun, color: 'text-orange-400' };
    }
    if (h >= 17 && h < 21) {
      return { label: 'Good evening', Icon: Moon, color: 'text-indigo-400' };
    }
    return { label: 'Working late', Icon: Coffee, color: 'text-amber-500' };
  }, []);
}

function LiveClock({ now }: { now: Date }) {
  const time = now.toLocaleTimeString(getLocaleSync(), { hour: 'numeric', minute: '2-digit' });
  const date = now.toLocaleDateString(getLocaleSync(), { weekday: 'short', day: 'numeric', month: 'short' });
  return (
    <span>
      {date} · {time}
    </span>
  );
}

